import { htmlToMessage } from "../editor/serialization";
import { NEW_EDITOR_STATE_SENTINEL } from "../editorStateSentinel";
import type { TemplateEditorMode, TemplateEditorValue } from "../TemplateEditor/types";

/**
 * The content-bearing subset of a `MessageTemplate` to persist, kept mutually consistent per the
 * serialization contract (§2 / §8).
 */
export interface TemplateContentFields {
  message: string;
  html: string;
  mode: TemplateEditorMode;
  editorState: string;
}

/**
 * Resolve the editor's live value into the contract-correct fields to save — the single canonical
 * place the `mode`↔field coupling lives, so consumers never re-derive it and drift from the contract.
 *
 * - `mode: 'html'` → the `html` string is the literal body, and `message` is **always** derived from
 *   it (via `htmlToMessage`) so the template still has a sendable plaintext body for a channel that
 *   reads only `message` (SMS). It must never be empty: a template can gain SMS as a target after it
 *   was authored, and a stale empty `message` would silently fail to send.
 * - `mode: 'richtext'` (the default) → `message` and `html` are the authored plaintext/HTML pair.
 *
 * `editorState` is **always** set to the new-editor sentinel, regardless of mode. It is not
 * reconstruction data: it is the marker the legacy Draft.js editor checks so it refuses to edit a
 * new-UI template inline (see {@link NEW_EDITOR_STATE_SENTINEL}). Omitting it — even in `mode: 'html'`
 * where the contract calls `editorState` unnecessary — would let the old UI treat its stale state as
 * authoritative and diverge from the current `message`/`html`, corrupting the body.
 *
 * Pure and idempotent — feeding its own output back in is a fixpoint (`htmlToMessage` is stable on
 * its own derived output). Not a hook; a reactive `useMemo` wrapper may be layered over it without
 * duplicating this logic.
 */
export const toTemplateContentFields = ({ message, html, mode }: TemplateEditorValue): TemplateContentFields =>
  mode === "html"
    ? { message: htmlToMessage(html), html, mode, editorState: NEW_EDITOR_STATE_SENTINEL }
    : { message, html, mode, editorState: NEW_EDITOR_STATE_SENTINEL };
